import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Clock, Flag, Bookmark, BookmarkCheck, Check, X, ChevronDown, ChevronUp, Maximize2, Minimize2 } from "lucide-react";
import { EnhancedExplanation } from "./EnhancedExplanation";
import { CoachLauncher } from "@/components/coaching/CoachLauncher";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { sanitizeHtml } from "@/lib/htmlSanitizer";

export interface MCQ {
  id: string;
  stem: string;
  options: string[];
  correctIndex: number;
  explanation?: string;
  tags?: string[];
  difficulty?: number;
}

interface MobileMCQCardProps {
  mcq: MCQ;
  onSubmit: (selectedIndex: number, isCorrect: boolean) => void;
  onClear?: () => void;
  timerEnabled?: boolean;
  mode?: "practice" | "exam" | "review";
  onFlag?: () => void;
  onBookmark?: () => void;
  isFlagged?: boolean;
  isBookmarked?: boolean;
  isFullScreen?: boolean;
  onToggleFullScreen?: () => void;
}

export const MobileMCQCard = ({
  mcq,
  onSubmit,
  onClear,
  timerEnabled = false,
  mode = "practice",
  onFlag,
  onBookmark,
  isFlagged = false,
  isBookmarked = false,
  isFullScreen = false,
  onToggleFullScreen
}: MobileMCQCardProps) => {
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [timer, setTimer] = useState(0);
  const [showExplanation, setShowExplanation] = useState(false);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (!timerEnabled || revealed) return;
    const id = setInterval(() => setTimer((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [timerEnabled, revealed]);

  const handleSubmit = () => {
    if (selectedAnswer === null) return;
    const isCorrect = selectedAnswer === mcq.correctIndex;
    onSubmit(selectedAnswer, isCorrect);
    setRevealed(true);
    setShowExplanation(true);
  };

  const reset = () => {
    setSelectedAnswer(null);
    setRevealed(false);
    setTimer(0);
    setShowExplanation(false);
    onClear?.();
  };

  const isCorrect = revealed && selectedAnswer === mcq.correctIndex;
  const isIncorrect = revealed && selectedAnswer !== null && selectedAnswer !== mcq.correctIndex;

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const optionLabels = ['A', 'B', 'C', 'D', 'E'];

  // Touch target size - minimum 48x48px
  const touchTargetClass = "min-h-[48px] touch-manipulation";

  return (
    <div className="medical-card p-4 space-y-4">
      {/* Compact Header */}
      <div className="flex items-center justify-between gap-2">
        {/* Timer */}
        {timerEnabled && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded-full">
            <Clock className="h-3 w-3" />
            <span className="font-mono">{formatTime(timer)}</span>
          </div>
        )}

        <div className="flex items-center gap-1 ml-auto">
          {/* Bookmark */}
          {onBookmark && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onBookmark}
              className={`${touchTargetClass} h-8 w-8 ${isBookmarked ? 'text-primary' : ''}`}
              aria-label={isBookmarked ? "Remove bookmark" : "Bookmark question"}
            >
              {isBookmarked ? <BookmarkCheck className="h-4 w-4" /> : <Bookmark className="h-4 w-4" />}
            </Button>
          )}

          {/* Flag */}
          {onFlag && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onFlag}
              className={`${touchTargetClass} h-8 w-8 ${isFlagged ? 'text-warning' : ''}`}
              aria-label={isFlagged ? "Unflag question" : "Flag for review"}
            >
              <Flag className="h-4 w-4" />
            </Button>
          )}

          {/* Full Screen Toggle */}
          {onToggleFullScreen && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggleFullScreen}
              className={`${touchTargetClass} h-8 w-8`}
              aria-label={isFullScreen ? "Exit full screen" : "Enter full screen"}
            >
              {isFullScreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </Button>
          )}
        </div>
      </div>

      {/* Question Stem - Sanitized to prevent XSS attacks */}
      <div className="prose prose-sm max-w-none dark:prose-invert">
        <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(mcq.stem) }} />
      </div>

      {/* Options - Large Touch Targets */}
      <div className="space-y-2">
        {mcq.options.map((option, idx) => {
          const isSelected = selectedAnswer === idx;
          const isThisCorrect = idx === mcq.correctIndex;
          const showAsCorrect = revealed && isThisCorrect;
          const showAsIncorrect = revealed && isSelected && !isThisCorrect;

          return (
            <button
              key={idx}
              onClick={() => !revealed && setSelectedAnswer(idx)}
              disabled={revealed}
              className={`
                ${touchTargetClass} w-full text-left px-4 py-3 rounded-xl border-2 
                transition-all duration-200
                ${!prefersReducedMotion && 'active:scale-[0.98]'}
                ${!revealed && isSelected ? 'border-primary bg-primary/5' : 'border-border'}
                ${showAsCorrect ? 'border-success bg-success/10' : ''}
                ${showAsIncorrect ? 'border-destructive bg-destructive/10' : ''}
                ${!revealed && !isSelected ? 'hover:border-primary/50 hover:bg-accent/5' : ''}
                disabled:cursor-not-allowed
              `}
              aria-label={`Option ${optionLabels[idx]}: ${option}`}
            >
              <div className="flex items-start gap-3">
                <span className={`
                  flex-shrink-0 w-7 h-7 rounded-full border-2 flex items-center justify-center text-sm font-semibold
                  ${showAsCorrect ? 'border-success bg-success text-white' : ''}
                  ${showAsIncorrect ? 'border-destructive bg-destructive text-white' : ''}
                  ${!revealed && isSelected ? 'border-primary bg-primary text-primary-foreground' : 'border-current'}
                `}>
                  {showAsCorrect ? <Check className="h-4 w-4" /> : 
                   showAsIncorrect ? <X className="h-4 w-4" /> : 
                   optionLabels[idx]}
                </span>
                <span className="flex-1 text-sm">{option}</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Sticky Bottom Actions */}
      <div className="sticky bottom-0 left-0 right-0 bg-background/95 backdrop-blur-sm pt-4 -mx-4 px-4 pb-4 border-t">
        <div className="flex gap-2">
          {!revealed ? (
            <Button
              onClick={handleSubmit}
              disabled={selectedAnswer === null}
              className={`${touchTargetClass} flex-1 font-semibold`}
              size="lg"
            >
              Submit Answer
            </Button>
          ) : (
            <>
              <Button
                onClick={reset}
                variant="outline"
                className={`${touchTargetClass} flex-1`}
                size="lg"
              >
                Try Again
              </Button>
              <CoachLauncher
                mcqId={mcq.id}
                topic={mcq.tags?.[0] || "General"}
                stem={mcq.stem}
                selectedOption={mcq.options[selectedAnswer || 0]}
                correctOption={mcq.options[mcq.correctIndex]}
                explanation={mcq.explanation || ""}
              />
            </>
          )}
        </div>
      </div>

      {/* Collapsible Explanation */}
      {revealed && mcq.explanation && (
        <div className="border-t pt-4 mt-4">
          <button
            onClick={() => setShowExplanation(!showExplanation)}
            className={`${touchTargetClass} w-full flex items-center justify-between text-sm font-medium py-2`}
            aria-expanded={showExplanation}
          >
            <span>Explanation</span>
            {showExplanation ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          
          {showExplanation && (
            <div className={`mt-3 ${!prefersReducedMotion && 'animate-fade-in'}`}>
              <EnhancedExplanation
                mcqId={mcq.id}
                question={mcq.stem}
                correctAnswer={mcq.options[mcq.correctIndex]}
                originalExplanation={mcq.explanation || ""}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
